import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { SiteWide, Images } from "./customComponents";
import { allRemoteSettingsSelect } from "../redux/selectors/index";
import parse from "html-react-parser";

import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import MonetizationOnOutlinedIcon from "@mui/icons-material/MonetizationOnOutlined";
import GavelIcon from "@mui/icons-material/Gavel";

function Term() {
  const navigate = useNavigate();
  const site_settings = useSelector(allRemoteSettingsSelect);
  const [hoveredBack, setHoveredBack] = useState(false);

  const features = [
    { icon: <LockOutlinedIcon style={{ fontSize: 20 }} />, text: "Data Privacy & Security Guaranteed" },
    { icon: <GavelIcon style={{ fontSize: 20 }} />, text: "Fair, Transparent & Safe Experience" },
    { icon: <ChatBubbleOutlineIcon style={{ fontSize: 20 }} />, text: "Respectful Community Guidelines" },
    { icon: <MonetizationOnOutlinedIcon style={{ fontSize: 20 }} />, text: "Transparent Reward Distributions" },
  ];

  return (
    <SiteWide>
      <div style={{
        minHeight: "100vh", display: "flex", alignItems: "stretch",
        background: "#f8fafc", fontFamily: "'DM Sans',sans-serif"
      }}>

        {/* ── Left panel (decorative) ── */}
        <div style={{
          flex: "0 0 38%", position: "relative", overflow: "hidden",
          background: "linear-gradient(135deg, #1e1b4b 0%, #312e81 40%, #4338ca 75%, #4f46e5 100%)",
          display: "flex", flexDirection: "column",
          padding: "48px 40px", boxSizing: "border-box"
        }}
          className="auth-left-panel"
        >
          {/* Ambient glow blobs */}
          <div style={{
            position: "absolute", top: -60, right: -60, width: 280, height: 280,
            borderRadius: "50%", background: "rgba(99,102,241,0.15)",
            filter: "blur(60px)"
          }} />
          <div style={{
            position: "absolute", bottom: 40, left: -40, width: 240, height: 240,
            borderRadius: "50%", background: "rgba(167,139,250,0.1)",
            filter: "blur(60px)"
          }} />

          {/* Logo */}
          <div style={{ position: "relative", zIndex: 1, marginBottom: "auto" }}>
            <Images
              height="44px"
              src={site_settings?.site_settings?.logo}
              alt="logo"
              style={{ filter: "brightness(0) invert(1)" }}
            />
          </div>

          {/* Hero text */}
          <div style={{ position: "relative", zIndex: 1, marginBottom: 48 }}>
            <h1 style={{
              color: "#ffffff", fontSize: 36, fontWeight: 800,
              fontFamily: "'Syne',sans-serif", lineHeight: 1.2,
              margin: "0 0 12px"
            }}>
              Terms &<br />Privacy<br />
              <span style={{ color: "#93c5fd" }}>Policy</span>
            </h1>
            <p style={{ color: "rgba(219,234,254,0.65)", fontSize: 14, margin: 0, lineHeight: 1.6 }}>
              Please review our official guidelines, user policies, and data protection practices.
            </p>
          </div>

          {/* Feature bullets */}
          <div style={{ position: "relative", zIndex: 1, display: "flex", flexDirection: "column", gap: 14 }}>
            {features.map((f, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{
                  width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                  background: "rgba(255,255,255,0.1)", backdropFilter: "blur(8px)",
                  border: "1px solid rgba(255,255,255,0.12)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  color: "#93c5fd"
                }}>
                  {f.icon}
                </div>
                <span style={{ color: "rgba(219,234,254,0.75)", fontSize: 13, fontFamily: "'DM Sans',sans-serif" }}>
                  {f.text}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* ── Right panel (terms text) ── */}
        <div style={{
          flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
          padding: "40px 24px", overflowY: "auto"
        }}>
          <div style={{ width: "100%", maxWidth: 720 }}>

            {/* Mobile logo */}
            <div style={{ textAlign: "center", marginBottom: 24, display: "none" }} className="auth-mobile-logo">
              <Images height="40px" src={site_settings?.site_settings?.logo} alt="logo" />
            </div>

            {/* Header & Back Button */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", marginBottom: 24 }}>
              <button
                onClick={() => navigate(-1)}
                onMouseEnter={() => setHoveredBack(true)}
                onMouseLeave={() => setHoveredBack(false)}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  background: hoveredBack ? "rgba(59,130,246,0.1)" : "rgba(59,130,246,0.06)",
                  border: "1px solid rgba(59,130,246,0.12)",
                  borderRadius: 12, padding: "8px 16px", cursor: "pointer",
                  color: "#2563eb", fontSize: 13, fontWeight: 600,
                  fontFamily: "'DM Sans',sans-serif", outline: "none",
                  transition: "all 0.2s"
                }}
              >
                <ChevronLeftIcon style={{ fontSize: 18 }} /> Back
              </button>
            </div>

            {/* Heading */}
            <h2 style={{
              color: "#0f172a", fontSize: 28, fontWeight: 800,
              fontFamily: "'Syne',sans-serif", margin: "0 0 6px"
            }}>
              Terms & Privacy Policy
            </h2>
            <p style={{ color: "#64748b", fontSize: 13, fontFamily: "'DM Sans',sans-serif", margin: "0 0 28px" }}>
              Please read carefully to understand your rights, responsibilities, and data privacy.
            </p>

            {/* Scrollable Terms & Policy Document */}
            <div style={{
              background: "#ffffff",
              border: "1.5px solid rgba(59,130,246,0.08)",
              borderRadius: 16,
              padding: "28px 24px",
              boxShadow: "0 4px 20px rgba(15, 23, 42, 0.03)",
              maxHeight: "60vh",
              overflowY: "auto",
              color: "#334155",
              fontSize: 14,
              lineHeight: 1.6,
              fontFamily: "'DM Sans',sans-serif"
            }} className="terms-content-scroll">
              {parse(site_settings?.site_settings?.term ?? '<p>No terms and conditions provided.</p>')}
            </div>

          </div>
        </div>
      </div>

      <style>{`
        .terms-content-scroll::-webkit-scrollbar { width: 6px; }
        .terms-content-scroll::-webkit-scrollbar-track { background: transparent; }
        .terms-content-scroll::-webkit-scrollbar-thumb { background: rgba(59, 130, 246, 0.15); border-radius: 10px; }
        .terms-content-scroll::-webkit-scrollbar-thumb:hover { background: rgba(59, 130, 246, 0.3); }
        .terms-content-scroll p { margin-bottom: 16px; color: #475569; }
        .terms-content-scroll h1, .terms-content-scroll h2, .terms-content-scroll h3, .terms-content-scroll h4, .terms-content-scroll h5 {
          font-family: 'Syne', sans-serif;
          color: #0f172a;
          font-weight: 700;
          margin-top: 24px;
          margin-bottom: 12px;
        }
        @media (max-width: 768px) {
          .auth-left-panel { display: none !important; }
          .auth-mobile-logo { display: block !important; }
        }
      `}</style>
    </SiteWide>
  );
}

export default Term;